import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Switch } from './ui/switch';
import {
  Bell, BellRing, MessageSquare, Calendar, Share2, CheckCheck,
  X, Settings as SettingsIcon, Loader2, Star, PhoneCall
} from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

const ICON_MAP = {
  new_message: { icon: MessageSquare, color: 'text-blue-500', bg: 'bg-blue-500/10' },
  appointment_created: { icon: Calendar, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
  appointment_reminder: { icon: Calendar, color: 'text-amber-500', bg: 'bg-amber-500/10' },
  social_published: { icon: Share2, color: 'text-purple-500', bg: 'bg-purple-500/10' },
  review_new: { icon: Star, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  phone: { icon: PhoneCall, color: 'text-primary', bg: 'bg-primary/10' },
};

const timeAgo = (dateStr) => {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'ora';
  if (diff < 3600) return `${Math.floor(diff / 60)}m fa`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h fa`;
  return `${Math.floor(diff / 86400)}g fa`;
};

const NotificationBell = ({ collapsed, topbar, testId = 'notification-bell' }) => {
  const { API, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const push = usePushNotifications();
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 1023px)').matches);
  const intervalRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Register service worker on mount
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/inbox`, getAuthHeaders());
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // Silently fail
    }
  }, [API, getAuthHeaders]);

  useEffect(() => {
    fetchNotifications();
    intervalRef.current = setInterval(fetchNotifications, 30000);
    return () => clearInterval(intervalRef.current);
  }, [fetchNotifications]);

  const markRead = async (notifId) => {
    try {
      await axios.put(`${API}/notifications/inbox/${notifId}/read`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/inbox/read-all`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      // Ignore
    }
  };

  const deleteNotif = async (e, notifId) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/notifications/inbox/${notifId}`, getAuthHeaders());
      const wasUnread = notifications.find(n => n.id === notifId && !n.is_read);
      setNotifications(prev => prev.filter(n => n.id !== notifId));
      if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // Ignore
    }
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.id);
    if (notif.link) {
      navigate(notif.link);
      setOpen(false);
    }
  };

  const pushSupported = push.supported;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`relative flex items-center ${collapsed ? 'justify-center w-10 h-10' : 'gap-3 w-full'} px-3 py-2.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-200`}
          data-testid={testId}
        >
          {unreadCount > 0 ? (
            <BellRing className="w-5 h-5 flex-shrink-0 text-primary animate-pulse" />
          ) : (
            <Bell className="w-5 h-5 flex-shrink-0" />
          )}
          {!collapsed && <span className="text-sm font-medium">Notifiche</span>}
          {unreadCount > 0 && (
            <span
              className={`absolute ${collapsed ? 'top-1 right-1' : 'top-1.5 left-6'} min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1`}
              data-testid={testId === 'notification-bell' ? 'notification-badge' : 'notification-badge-mobile'}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(22rem,calc(100vw-2rem))] lg:w-96 p-0 max-h-[85vh] flex flex-col overflow-hidden"
        side={isMobile || topbar ? 'bottom' : 'right'}
        align={isMobile ? 'center' : 'end'}
        sideOffset={8}
        collisionPadding={16}
        data-testid="notification-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold font-outfit text-sm">Notifiche</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button variant="ghost" size="sm" className="text-xs h-7 px-2" onClick={markAllRead} data-testid="mark-all-read-btn">
                <CheckCheck className="w-3.5 h-3.5 mr-1" />Segna tutte lette
              </Button>
            )}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
              onClick={() => { setShowSettings(s => !s); push.refresh(); }} data-testid="notification-settings-btn">
              <SettingsIcon className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Push Settings */}
        {showSettings && (
          <div className="px-4 py-3 border-b bg-muted/30 space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">Notifiche Push</p>
                <p className="text-xs text-muted-foreground">
                  {!pushSupported
                    ? 'Non supportato dal tuo browser'
                    : !push.configured
                      ? 'Non configurate sul server'
                      : 'Ricevi notifiche anche quando il browser è chiuso'}
                </p>
              </div>
              <Switch
                checked={push.enabled}
                onCheckedChange={push.toggle}
                disabled={!pushSupported || push.busy}
                data-testid="push-toggle"
              />
            </div>
            <p className="text-xs text-muted-foreground" data-testid="push-devices">
              {push.devices === 0
                ? 'Nessun dispositivo collegato'
                : `${push.devices} dispositiv${push.devices === 1 ? 'o collegato' : 'i collegati'}`}
            </p>
            {push.enabled && (
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="text-xs w-full min-h-[40px]"
                  onClick={push.sendTest} disabled={push.busy} data-testid="test-push-btn">
                  {push.busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <BellRing className="w-3.5 h-3.5 mr-1.5" />}
                  Invia notifica di test
                </Button>
                {push.devices > 1 && (
                  <Button variant="ghost" size="sm" className="text-xs w-full min-h-[40px]"
                    onClick={push.removeOtherDevices} disabled={push.busy} data-testid="remove-other-devices-btn">
                    Scollega gli altri dispositivi
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Notification List */}
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain" data-testid="notification-scroll">
          {notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notif) => {
                const config = ICON_MAP[notif.type] || ICON_MAP.new_message;
                const Icon = config.icon;
                return (
                  <div
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50 ${!notif.is_read ? 'bg-primary/5' : ''}`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                      <Icon className={`w-4 h-4 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm truncate ${!notif.is_read ? 'font-semibold' : 'font-medium text-muted-foreground'}`}>
                          {notif.title}
                        </p>
                        <button
                          onClick={(e) => deleteNotif(e, notif.id)}
                          className="opacity-100 lg:opacity-0 lg:group-hover:opacity-100 hover:text-destructive p-1 flex-shrink-0 transition-opacity"
                          data-testid={`delete-notif-${notif.id}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-0.5">{notif.message}</p>
                      <p className="text-[10px] text-muted-foreground/60 mt-1">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Bell className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
              <p className="text-sm text-muted-foreground">Nessuna notifica</p>
              <p className="text-xs text-muted-foreground/60 mt-1">Le notifiche appariranno qui</p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
